import { useQuery } from "@apollo/client";
import { Authorized } from "Components/Authorized";
import * as types from "_graphql-types/graphql";
import classnames from "classnames";
import i18n from "i18next";
import { Link, useLocation } from "react-router-dom";
import { useGlobalCompanySearch } from "./CompanySearch.hooks";
import { useGlobalEngagementSearch } from "./EngagementSearch.hooks";
import { COMPANY_PATHS } from "./GlobalSearchControls.component";
import {
  useGlobalInvestmentSearch,
  usePortfolioSearch,
} from "./InvestmentSearch.hooks";
import { COMPANY_SEARCH } from "./graphql";

function Tab({
  count,
  name,
  path,
}: {
  count?: number;
  name: string;
  path: string;
}) {
  const location = useLocation();
  const label = i18n.t(`category_tabs.${name}.title`, { count });
  const displayLabel =
    count !== undefined && count >= 9999
      ? label.replace(String(count), "9999+")
      : label;

  return (
    <li
      className={classnames("main-tabs__tab", {
        "main-tabs__tab--active": path === location.pathname,
        company: COMPANY_PATHS.includes(location.pathname),
      })}
      data-cy={`${name}-tab`}
    >
      <Link to={path} style={{ color: "inherit" }}>
        {displayLabel}
      </Link>
    </li>
  );
}

function Portfolios() {
  const { result } = usePortfolioSearch();
  if (!result?.total) return null;
  return <Tab count={result?.total} name="portfolios" path="/portfolios" />;
}

function InvestmentSearchTotals() {
  const { result } = useGlobalInvestmentSearch();
  return <Tab count={result?.total} name="investments" path="/investments" />;
}

function CompanySearchTotals() {
  const { variables } = useGlobalCompanySearch();

  const { data: companiesData } = useQuery<{ companyList: types.CompanyList }>(
    COMPANY_SEARCH,
    {
      variables: {
        searchFilters: variables.searchFilters,
        sort: [
          {
            field: "latestUpdate",
            order: types.SortInput.Desc,
          },
        ],
        page: {
          limit: 15,
          offset: 0,
        },
      },
    }
  );

  return (
    <>
      <Tab
        count={companiesData?.companyList.total}
        name="companies"
        path="/company"
      />
    </>
  );
}

function EngagementSearchTotals() {
  const { result } = useGlobalEngagementSearch({ limit: 1, offset: 0 });
  return (
    <Tab
      count={result?.total}
      name="engagements"
      path="/engagementsSearch"
    />
  );
}

function DiligenceTab() {
  return <Tab name="diligence" path="/diligenceSearch" />;
}

function KnowledgeBaseTab() {
  return <Tab name="knowledgeBase" path="/knowledgeBase" />;
}

function GlobalSearchTotals() {
  return (
    <div className="main-gf__categories-tabs-wrap">
      <div className="main-gf__categories-tabs">
        <ul className="pt-4 horizontal-scroll">
          <Portfolios />
          <Authorized checks={["investmentPageAccess"]}>
            <InvestmentSearchTotals />
          </Authorized>
          <Authorized checks={["isSubscriptionUser"]}>
            <CompanySearchTotals />
          </Authorized>
          <Authorized checks={["isSubscriptionUser"]}>
            <EngagementSearchTotals />
          </Authorized>
          <Authorized checks={["isSubscriptionUser"]}>
            <KnowledgeBaseTab />
          </Authorized>
          <Authorized checks={["isSubscriptionUser"]}>
            <DiligenceTab />
          </Authorized>
        </ul>
      </div>
    </div>
  );
}

export default GlobalSearchTotals;
